import React, { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, Search, MapPin, Globe, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { formatDisplayDate } from '@/lib/utils';
import { State, StateFormData, Country } from '@/types/geography';
import { useConfirmDialog, createDeleteConfirm, createDeactivateConfirm } from '@/components/ui/confirm-dialog';
import DataTablePagination from '@/components/ui/data-table-pagination';
import {
  useStates as useStatesApi,
  useState as useStateApi,
  useCreateState,
  useUpdateState,
  useDeleteState,
} from '@/api-services/hooks/useStateRefactored';
import { useCountries as useCountriesApi } from '@/api-services/hooks/useCountryRefactored';
import {
  buildStatePayload,
  mapStateMasterToState,
  mapStateToPayload,
  mapStatusToApi,
  mapCountryMasterToCountry,
} from '@/utils/geographyMappers';
import { API_BASE_URL } from '@/api-services/config';
import type { StateMaster } from '@/api-services/hooks/types';
const StatesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [states, setStates] = useState<State[]>([]);
  const [filteredStates, setFilteredStates] = useState<State[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingState, setEditingState] = useState<State | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [countryFilter, setCountryFilter] = useState<string>('all');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const { confirm, ConfirmDialog } = useConfirmDialog();

  // Form state
  const [formData, setFormData] = useState<StateFormData>({
    name: '',
    code: '',
    countryId: '',
    status: 'active'
  });

  // Validation errors state
  const [errors, setErrors] = useState<{
    name?: string;
    code?: string;
    countryId?: string;
    status?: string;
  }>({});

  // Validation function
  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'State name is required';
    } else {
      // Check for unique name (case-insensitive) globally across all countries
      const isDuplicateName = states.some(
        state => state.name.toLowerCase() === formData.name.toLowerCase() &&
        state.id !== editingState?.id
      );
      if (isDuplicateName) {
        newErrors.name = 'State with this name already exists';
      }
    }

    // Code validation
    if (!formData.code.trim()) {
      newErrors.code = 'State code is required';
    } else if (formData.code.length < 2) {
      newErrors.code = 'State code must be at least 2 characters';
    } else if (formData.code.length > 5) {
      newErrors.code = 'State code must be no more than 5 characters';
    } else {
      // Check for unique code (case-insensitive) globally across all countries
      const isDuplicateCode = states.some(
        state => state.code.toLowerCase() === formData.code.toLowerCase() &&
        state.id !== editingState?.id
      );
      if (isDuplicateCode) {
        newErrors.code = 'State with this code already exists';
      }
    }

    // Country validation
    if (!formData.countryId) {
      newErrors.countryId = 'Country is required';
    }

    // Status validation
    if (!formData.status) {
      newErrors.status = 'Status is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Real-time validation on input change
  const handleInputChange = (field: keyof StateFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const stateQueryParams = useMemo(() => ({ page: 0, size: 1000 }), []);
  const countryQueryParams = useMemo(() => ({ page: 0, size: 1000 }), []);

  const {
    data: stateResponse,
    loading: statesLoading,
    error: statesError,
    refetch: refetchStates,
  } = useStatesApi(stateQueryParams);

  const {
    data: countryResponse,
    loading: countriesLoading,
    error: countriesError,
  } = useCountriesApi(countryQueryParams);

  const createStateMutation = useCreateState();
  const updateStateMutation = useUpdateState();
  const deleteStateMutation = useDeleteState();

  // Fetch existing state data when editing to preserve system IP fields
  const { data: existingStateData } = useStateApi(
    editingState ? Number(editingState.id) : 0,
    {
      enabled: !!editingState && !!editingState.id,
    }
  );

  const actionLoading =
    createStateMutation.loading ||
    updateStateMutation.loading ||
    deleteStateMutation.loading;

  useEffect(() => {
    if (stateResponse?.content) {
      const mappedStates = stateResponse.content.map(mapStateMasterToState);
      setStates(mappedStates);
    } else {
      setStates([]);
    }
  }, [stateResponse]);

  useEffect(() => {
    if (countryResponse?.content) {
      const mappedCountries = countryResponse.content.map(mapCountryMasterToCountry);
      setCountries(mappedCountries);
    } else {
      setCountries([]);
    }
  }, [countryResponse]);

  useEffect(() => {
    if (statesError) {
      toast.error(statesError.message);
    }
  }, [statesError]);

  useEffect(() => {
    if (countriesError) {
      toast.error(countriesError.message);
    }
  }, [countriesError]);

  // Filter states based on search, status, and country
  useEffect(() => {
    let filtered = states;

    if (searchTerm) {
      filtered = filtered.filter(state =>
        state.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        state.code.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(state => state.status === statusFilter);
    }

    if (countryFilter !== 'all') {
      filtered = filtered.filter(state => state.countryId === countryFilter);
    }

    setFilteredStates(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [states, searchTerm, statusFilter, countryFilter]);

  // Pagination calculations
  const totalItems = filteredStates.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedStates = filteredStates.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when items per page changes
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      countryId: '',
      status: 'active'
    });
    setErrors({});
    setEditingState(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      toast.error('Please fix the validation errors');
      return;
    }

    try {
      if (editingState) {
        const payload = await buildStatePayload(formData, editingState.id, existingStateData || undefined);
        await updateStateMutation.mutate({
          id: Number(editingState.id),
          data: payload,
        });
        toast.success('State updated successfully!');
      } else {
        const payload = await buildStatePayload(formData);
        await createStateMutation.mutate(payload);
        toast.success('State created successfully!');
      }

      await refetchStates();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save state';

      // Handle server-side validation errors for duplicates
      if (message.toLowerCase().includes('duplicate') ||
          message.toLowerCase().includes('already exists') ||
          message.toLowerCase().includes('unique')) {
        if (message.toLowerCase().includes('name')) {
          setErrors(prev => ({ ...prev, name: 'State name already exists' }));
        } else if (message.toLowerCase().includes('code')) {
          setErrors(prev => ({ ...prev, code: 'State code already exists' }));
        } else {
          // Generic duplicate error
          setErrors(prev => ({
            ...prev,
            name: 'Duplicate state detected',
            code: 'Duplicate state detected'
          }));
        }
      }

      toast.error(message);
    }
  };

  const handleEdit = (state: State) => {
    setEditingState(state);
    setFormData({
      name: state.name,
      code: state.code,
      countryId: state.countryId,
      status: state.status,
      metadata: state.metadata || {}
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (state: State) => {
    if (actionLoading) return;
    const stateName = state.name;
    confirm(createDeleteConfirm(
      'State',
      async () => {
        try {
          await deleteStateMutation.mutate(Number(state.id));
          toast.success(`${stateName} deleted successfully!`);
          await refetchStates();
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to delete state';
          toast.error(message);
        }
      },
      `Are you sure you want to delete ${stateName}? This will permanently remove the state and all associated data. This action cannot be undone.`
    ));
  };

  const handleStatusToggle = (state: State) => {
    if (actionLoading) return;
    const isActive = state.status === 'active';
    const stateName = state.name;

    confirm(createDeactivateConfirm(
      'State',
      isActive,
      async () => {
        try {
          const newStatus = isActive ? 'inactive' : 'active';

          // Optimistically update the cache immediately
          queryClient.setQueriesData(
            { queryKey: ['states'] },
            (oldData: any) => {
              if (!oldData?.content) return oldData;

              const updatedContent = oldData.content.map((item: any) =>
                item.stateId === state.id
                  ? { ...item, isActive: newStatus === 'active' }
                  : item
              );

              return {
                ...oldData,
                content: updatedContent,
              };
            }
          );

          // Fetch existing state data to preserve system IP fields
          let existingStateData: StateMaster | undefined;
          try {
            const response = await fetch(`${API_BASE_URL}/state/${state.id}`);
            if (response.ok) {
              const apiResponse = await response.json();
              existingStateData = apiResponse.data;
            }
          } catch (err) {
            // Silently handle fetch error
          }
          const basePayload = await mapStateToPayload(state, existingStateData);
          const payload = {
            ...basePayload,
            status: mapStatusToApi(newStatus),
          };
          await updateStateMutation.mutate({
            id: Number(state.id),
            data: payload,
          });
          toast.success(`${stateName} ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully!`);
        } catch (error) {
          // Revert optimistic update on error
          queryClient.setQueriesData(
            { queryKey: ['states'] },
            (oldData: any) => {
              if (!oldData?.content) return oldData;

              const updatedContent = oldData.content.map((item: any) =>
                item.stateId === state.id
                  ? { ...item, isActive: isActive }
                  : item
              );

              return {
                ...oldData,
                content: updatedContent,
              };
            }
          );
          const message = error instanceof Error ? error.message : 'Failed to update state status';
          toast.error(message);
        }
      }
    ));
  };

  const getCountryName = (countryId: string) => {
    const country = countries.find(c => c.id === countryId);
    return country ? country.name : 'Unknown';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">States</h1>
          <p className="text-muted-foreground">Manage states and provinces</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="w-4 h-4 mr-2" />
              Add State
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingState ? 'Edit State' : 'Add New State'}
              </DialogTitle>
              <DialogDescription>
                {editingState
                  ? 'Update the state information below.'
                  : 'Add a new state or province to the system.'
                }
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">State/Province Name *</label>
                  <Input
                    key="state-name-input"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    onBlur={() => {
                      // Validate on blur for better UX
                      if (formData.name.trim()) {
                        const isDuplicateName = states.some(
                          state => state.name.toLowerCase() === formData.name.toLowerCase() &&
                          state.id !== editingState?.id
                        );
                        if (isDuplicateName) {
                          setErrors(prev => ({ ...prev, name: 'State with this name already exists' }));
                        }
                      }
                    }}
                    placeholder="California"
                    className={errors.name ? "border-red-500 focus:ring-red-500" : ""}
                    required
                  />
                  {errors.name && (
                    <p className="text-sm text-red-600 mt-1">{errors.name}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium">Code *</label>
                  <Input
                    key="state-code-input"
                    value={formData.code}
                    onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                    onBlur={() => {
                      // Validate on blur for better UX
                      if (formData.code.trim()) {
                        if (formData.code.length < 2) {
                          setErrors(prev => ({ ...prev, code: 'State code must be at least 2 characters' }));
                        } else if (formData.code.length > 5) {
                          setErrors(prev => ({ ...prev, code: 'State code must be no more than 5 characters' }));
                        } else {
                          const isDuplicateCode = states.some(
                            state => state.code.toLowerCase() === formData.code.toLowerCase() &&
                            state.id !== editingState?.id
                          );
                          if (isDuplicateCode) {
                            setErrors(prev => ({ ...prev, code: 'State with this code already exists' }));
                          }
                        }
                      }
                    }}
                    placeholder="CA"
                    maxLength={5}
                    className={errors.code ? "border-red-500 focus:ring-red-500" : ""}
                    required
                  />
                  {errors.code && (
                    <p className="text-sm text-red-600 mt-1">{errors.code}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Country *</label>
                <Select
                  value={formData.countryId}
                  onValueChange={(value) => {
                    handleInputChange('countryId', value);
                  }}
                >
                  <SelectTrigger disabled={countriesLoading || countries.length === 0} className={errors.countryId ? "border-red-500 focus:ring-red-500" : ""}>
                    <SelectValue placeholder="Select a country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem key={country.id} value={country.id}>
                        {country.name} ({country.code})
                      </SelectItem>
                    ))}
                    {!countriesLoading && countries.length === 0 && (
                      <SelectItem value="no-country" disabled>
                        No countries available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {errors.countryId && (
                  <p className="text-sm text-red-600 mt-1">{errors.countryId}</p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium">Status *</label>
                <Select
                  value={formData.status}
                  onValueChange={(value: 'active' | 'inactive') => {
                    handleInputChange('status', value);
                  }}
                >
                  <SelectTrigger className={errors.status ? "border-red-500 focus:ring-red-500" : ""}>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
                {errors.status && (
                  <p className="text-sm text-red-600 mt-1">{errors.status}</p>
                )}
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={actionLoading}>
                  {actionLoading ? 'Saving...' : (editingState ? 'Update' : 'Create')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total States</CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{states.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active States</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {states.filter(s => s.status === 'active').length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Inactive States</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {states.filter(s => s.status === 'inactive').length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Countries</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {new Set(states.map(s => s.countryId)).size}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search states..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={countryFilter} onValueChange={setCountryFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by country" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Countries</SelectItem>
                {countries.map((country) => (
                  <SelectItem key={country.id} value={country.id}>
                    {country.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* States Table */}
      <Card>
        <CardHeader>
          <CardTitle>States ({filteredStates.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {statesLoading && (
            <div className="text-sm text-muted-foreground mb-4">Loading states...</div>
          )}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>State/Province</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Country</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedStates.map((state) => (
                <TableRow key={state.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{state.name}</div>
                      {state.metadata?.capital && (
                        <div className="text-sm text-muted-foreground">
                          Capital: {state.metadata.capital}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{state.code}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-muted-foreground" />
                      {getCountryName(state.countryId)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={state.status === 'active' ? 'default' : 'secondary'}
                      className="cursor-pointer"
                      onClick={() => handleStatusToggle(state)}
                    >
                      {state.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-muted-foreground">
                      {formatDisplayDate(state.createdAt)}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(state)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(state)}
                        disabled={actionLoading}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {!statesLoading && filteredStates.length === 0 && (
            <div className="text-center py-8">
              <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium">No states found</h3>
              <p className="text-muted-foreground">
                {searchTerm || statusFilter !== 'all' || countryFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Get started by adding your first state'}
              </p>
            </div>
          )}

          {/* Pagination */}
          {filteredStates.length > 0 && (
            <DataTablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              className="mt-4"
            />
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <ConfirmDialog />
    </div>
  );
};

export default StatesPage;
